import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Table, Tag, Card, DatePicker, Space, Spin, Button } from "antd";
import { CalendarOutlined, EyeOutlined } from "@ant-design/icons";
import Avatar from "../../../assets/avatars/avatar.png";
import { fetchProcessedReservation } from "../../../utils/api.js";
import dayjs from "dayjs";
import ViewPost from "./ViewPoster.js";
import "../../../App.css";

const { Column, ColumnGroup } = Table;

const ProcessedReservation = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [, setCurrentTime] = useState(moment(Date.now()));
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [selectedRecord, setSelectedRecord] = useState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const onChange = (date, dateString) => {
    if (dateString) {
      setSelectedDate(dateString);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const { data, isLoading: isInitialLoading } = useQuery({
    queryKey: ["processedReserve", currentPage, pageSize, selectedDate],
    queryFn: () =>
      fetchProcessedReservation({
        date: selectedDate,
        page: currentPage,
        pageSize,
      }),
    keepPreviousData: true,
  });

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleViewPoster = (record) => {
    setSelectedRecord(record);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRecord(null);
  };

  const dataSource = data?.results?.map((reservation) => {
    const house = reservation.house || {};
    const postedBy = house.postedBy?.userAccount || {};
    const user = reservation.user || {};

    const createdAtUTC = moment.utc(reservation.created_at);
    const createdAtEAT = createdAtUTC.clone().add(3, "hours");

    return {
      key: reservation.id,
      user: `${user.userAccount?.first_name} ${user.userAccount?.last_name}`,
      fullName: `${postedBy.first_name || "N/A"} ${postedBy.last_name}`,
      username: house.postedBy?.userAccount?.username,
      houseTitle: house.title || "N/A",
      CheckIn: reservation.checkIn,
      CheckOut: reservation.checkOut,
      type_proof_of_identity: reservation.type_proof_of_identity,
      city: reservation.house.city,
      typeofHouse: reservation.house.typeofHouse,
      price: reservation.house.price,
     // username: reservation.house.postedBy.userAccount.username,
      specificAddress: reservation.house?.specificAddress,
      createdAt: reservation.createdAt,
      phoneNumber: user?.phoneNumber || "N/A",
      decisionTime: moment(reservation.decisionTime).format("YYYY-MM-DD HH:mm"),
      status: reservation.status,
      reservationDate: moment(reservation.created_at).format(
        "YYYY-MM-DD HH:mm"
      ),
      timeElapsed: reservation.created_at,
      createdAtEAT: createdAtEAT,
      image: house.houseImage?.[0]?.image || Avatar,
      houseImage: house.houseImage || [],
      customerName: `${user.userAccount?.first_name} ${user.userAccount?.last_name}`,
      postedByPhone: house.postedBy?.phoneNumber || "N/A",
      postedByOptional: house.postedBy?.optional_phone_number || "N/A",
      postedByProfile : house.postedBy?.profilePicture,
      postedById: house.postedBy?.id
    };
  });

  return (
    <>
      {isInitialLoading ? (
        <Card>
          <Spin /> Loading Processed Reservations...
        </Card>
      ) : (
        <Card style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}>
          {/* Date Filter */}
          <Space direction="vertical" style={{ marginBottom: "16px" }}>
            <DatePicker
              onChange={onChange}
              // value={moment(selectedDate, "YYYY-MM-DD")} // Controlled value
              format="YYYY-MM-DD"
              allowClear={false}
              style={{ width: "100%" }}
              defaultValue={dayjs()}
            />
          </Space>

          {/* Table */}
          <Table
            dataSource={dataSource}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: data?.totalCount || 0,
              onChange: handlePageChange,
              showSizeChanger: true,
              responsive: true,
            }}
            scroll={{ x: true }}
            className="responsive-table"
          >
            <ColumnGroup>
              <Column
                title="Booking ID"
                key="user"
                render={(record) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <span>{record.key}</span>
                  </div>
                )}
              />
              <Column
                title="Customer Name"
                key="fullName"
                render={(record) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <img
                      src={record.image}
                      alt="Avatar"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <span>{record.fullName}</span>
                  </div>
                )}
              />
              <Column
                title="Property name"
                key="houseTitle"
                dataIndex="houseTitle"
              />
              <Column
                title="Check In"
                dataIndex="CheckIn"
                key="CheckIn"
                render={(CheckIn) => (
                  <span>
                    <CalendarOutlined /> {CheckIn}
                  </span>
                )}
              />
              <Column
                title="Check Out"
                dataIndex="CheckOut"
                key="CheckOut"
                render={(CheckOut) => (
                  <span>
                    <CalendarOutlined /> {CheckOut}
                  </span>
                )}
              />
              <Column
                title="Decision Time"
                dataIndex="decisionTime"
                key="decisionTime"
              />
              <Column
                title="Reservation Time"
                dataIndex="reservationDate"
                key="reservationDate"
              />
              <Column
                title="Status"
                dataIndex="status"
                key="status"
                render={(status) => (
                  <Tag
                    color={status === "Approved" ? "green" : "gold"}
                    key={status}
                  >
                    {status || "Pending"}
                  </Tag>
                )}
              />
              <Column
                title="Time Elapsed"
                key="timeElapsed"
                render={(record) => {
                  // Parse the createdAt time as a moment object in UTC then add +3 to get EAT
                  const createdAtEAT = moment
                    .utc(record.createdAt)
                    .add(3, "hours");

                  // Get current time in EAT
                  const nowEAT = moment.utc().add(3, "hours");

                  // Calculate duration
                  const duration = moment.duration(nowEAT.diff(createdAtEAT));

                  return <span>{duration.humanize()} ago</span>;
                }}
              />

              <Column
                title="Action"
                key="action"
                render={(_, record) => (
                  <Space size="middle" style={{ color: "blue" }}>
                    <Button
                      type="default"
                      className="text-[#55497C] border-[#55497C]"
                      variant="outlined"
                      onClick={() => handleViewPoster(record)}
                    >
                      <EyeOutlined />
                      View
                    </Button>
                  </Space>
                )}
              />
            </ColumnGroup>
          </Table>
        </Card>
      )}
      {selectedRecord && (
        <ViewPost
          open={isDrawerOpen}
          onClose={closeDrawer}
          record={selectedRecord}
        />
      )}
    </>
  );
};

export default ProcessedReservation;
